import React, { useState, useCallback, useContext, useEffect, useMemo } from 'react';
import { Handle, Position, useReactFlow, useEdges, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { GiMoneyStack } from "react-icons/gi";
import { GrConfigure } from 'react-icons/gr';
import './FinanceNode.css'; // Import the CSS file
import FinanceConfigModal from './FinanceConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { reconfigureNode } from '../../../api/api';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';

const FinanceNode = ({ id, data, selected }) => {
  const { perUnitDiscomCost, name } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [tempFormValues, setTempFormValues] = useState({});
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addFinanceNode, updateSelectedNode } = usePipeline();

  // Prefer session-updated tariff (from Setup Utility saves), fallback to node data
  const displayTariff = useMemo(() => {
    try {
      const n = (session?.nodes || []).find(n => n.node_name === name && n.node_type === 'finance');
      const v = n && n.node_params && n.node_params[0] && n.node_params[0].per_unit_discom_cost;
      if (v !== undefined && v !== null && v !== '') {
        const num = Number(v);
        if (!Number.isNaN(num)) return Math.round(num * 10000) / 10000;
        return v;
      }
    } catch (e) {}
    const fallback = Number(perUnitDiscomCost);
    return Number.isNaN(fallback) ? perUnitDiscomCost : Math.round(fallback * 10000) / 10000;
  }, [session, name, perUnitDiscomCost]);
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`FinanceNode ${id} received data:`, data);
    console.log(`Individual values - perUnitDiscomCost: ${perUnitDiscomCost}`);
    console.log(`Value types - perUnitDiscomCost: ${typeof perUnitDiscomCost}`);
  }, [id, data, perUnitDiscomCost]);

  // Check if node is configured properly
  // Either explicitly marked as configured or has name AND required parameters
  const isConfigured = data.isConfigured || (!!name && !!perUnitDiscomCost);
  
  // Check if this node has an input connection
  const hasInputConnection = edges.some(edge => edge.target === id);
  
  // Get the node mapping from context
  const { getNodeMapping } = useContext(NodeMappingContext);
  
  // Get the input node name if there's a connection
  const getInputNodeName = useCallback(() => {
    const inputEdge = edges.find(edge => edge.target === id);
    if (!inputEdge) return null;
    
    const sourceNodeId = inputEdge.source;
    const sourceNode = nodes.find(node => node.id === sourceNodeId);
    
    if (!sourceNode) return null;
    
    // First try to get the name from the node data
    if (sourceNode.data && sourceNode.data.name) {
      return sourceNode.data.name;
    }
    
    // If not found in data, try to get it from the node mapping
    const mappedName = getNodeMapping()[sourceNodeId];
    if (mappedName) {
      return mappedName;
    }
    
    // If all else fails, use the node ID
    return sourceNodeId;
  }, [edges, id, nodes, getNodeMapping]);
  
  // Get the current input node name
  const inputNodeName = getInputNodeName();
  
  // Check if the node needs reconfiguration
  const needsReconfiguration = 
    // Explicitly marked as needing reconfiguration
    data.isApiConfigured === false || 
    // Was configured but now has no input connection
    (isConfigured && !hasInputConnection && data.inputNodeName) ||
    // Input node has changed from what was previously configured
    (isConfigured && data.inputNodeName && inputNodeName && data.inputNodeName !== inputNodeName);
  
  // Function to handle reconfiguration of the input node
  const handleReconfigureInput = async () => {
    if (!session.pipeline_name || !name) return;
    
    try {
      setIsConfiguring(true);
      
      // Get the current input node name
      const inputNodeName = getInputNodeName();
      
      if (!inputNodeName) {
        throw new Error('No input node connected');
      }
      
      console.log(`Reconfiguring node ${name} with input node ${inputNodeName}`);
      console.log(`Previous input node was: ${data.inputNodeName || 'unknown'}`);
      
      // Call the API to update the input node
      await reconfigureNode(
        session.pipeline_name,
        name,
        inputNodeName
      );
      
      console.log(`Node ${name} input reconfigured successfully from ${data.inputNodeName || 'unknown'} to ${inputNodeName}`);
      
      // Update the node data to mark it as configured
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                isConfigured: true,
                isApiConfigured: true,
                inputNodeName: inputNodeName
              }
            };
          }
          return node;
        })
      );
      
      // Also update the session state
      const position = getNodePosition();
      
              addFinanceNode(
          name,
          data.perUnitDiscomCost || '0',
          inputNodeName,
          { x: position.x, y: position.y }
        );
      
      setIsConfiguring(false);
    } catch (error) {
      console.error('Error reconfiguring node input:', error);
      notifyError(error, { operation: OP.node.reconfigure });
      setIsConfiguring(false);
    }
  };

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback((isEditMode = false) => {
    // Log the current state for debugging
    console.log("openConfigModal called with state:", {
      hasInputConnection,
      isConfigured,
      needsReconfiguration,
      isModalOpen,
      isEditMode
    });
    
    if (!isEditMode && !hasInputConnection && !isConfigured) {
      // Show an alert if there's no input connection and we're not already configured
      // Skip this check in edit mode
      notifyError(new Error("Please connect an input to this finance node before configuring it."), {
        toastId: 'finance.connectInput',
      });
      return;
    }
    
    // Open the configuration modal
    let modalMode = 'configuration';
    if (needsReconfiguration) modalMode = 'reconfiguration';
    if (isEditMode) modalMode = 'editing';
    
    console.log(`Opening modal for ${modalMode}`);
    
    // Force a small delay to ensure React state updates properly
    setTimeout(() => {
      setIsModalOpen(true);
      console.log("Modal should be open now:", { isModalOpen: true, modalMode });
    }, 50);
  }, [hasInputConnection, isConfigured, needsReconfiguration, isModalOpen]);

  const closeConfigModal = useCallback((formValues) => {
    if (formValues && !data.isConfigured) {
      setTempFormValues(formValues);
    }
    setIsModalOpen(false);
  }, [data.isConfigured]);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const { name, discomCharge } = configData;
      const inputNodeName = getInputNodeName();
      const position = getNodePosition();
      
      // Log the configuration data for debugging
      console.log(`Configuring finance node ${id} -> ${name}:`, {
        configData,
        inputNodeName,
        position,
        edges: edges.filter(edge => edge.target === id || edge.source === id),
        isConfigured: data.isConfigured,
        isApiConfigured: data.isApiConfigured,
        needsReconfiguration
      });
      
              // Add the finance node to the session state
        if (inputNodeName) {
          // First update the node ID to name mapping before adding to session
          // This ensures the correct name is used in the pipeline state
          updateNodeMapping(id, name);
          console.log(`Updated node mapping before adding to session: ${id} -> ${name}`);
          
          // Find the source node in the nodes array to get its actual name
          const inputEdge = edges.find(edge => edge.target === id);
          const sourceNodeId = inputEdge ? inputEdge.source : null;
          const sourceNode = sourceNodeId ? nodes.find(node => node.id === sourceNodeId) : null;
          
          // Determine the actual input node name to use
          let actualInputNodeName = inputNodeName;
          
          // If the source node has a name in its data, use that
          if (sourceNode && sourceNode.data && sourceNode.data.name) {
            actualInputNodeName = sourceNode.data.name;
            console.log(`Using source node name from data: ${actualInputNodeName}`);
          }
          
          // Check if this is a reconfiguration (node already exists but input changed)
          const isReconfiguration = needsReconfiguration;
          
          console.log(`${isReconfiguration ? 'Reconfiguring' : 'Adding'} finance node to session:`, {
            name,
            perUnitDiscomCost,
            inputNodeName: actualInputNodeName,
            position,
            isReconfiguration
          });
          
          // Now add/update the node in the session with the correct name
          addFinanceNode(
            name,
            perUnitDiscomCost,
            actualInputNodeName,
            { x: position.x, y: position.y }
          );
        
        // Update the node data with the new configuration
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  perUnitDiscomCost,
                  isConfigured: true,
                  isApiConfigured: true,
                  inputNodeName: actualInputNodeName // Store the input node name for future reference
                }
              };
            }
            return node;
          })
        );
        
        // If the node was previously selected, update the selection with the new name
        if (session.selected_node === id) {
          console.log(`Node was selected, updating selection from ${id} to ${name}`);
          updateSelectedNode(name);
        }
      } else {
        // Just update the UI if we're not connected to a backend
        // Still update the mapping first
        updateNodeMapping(id, name);
        
        // Update the node data with the new configuration
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  name,
                  perUnitDiscomCost,
                  isConfigured: true
                }
              };
            }
            return node;
          })
        );
      }
      
      // End the configuration process
      setIsConfiguring(false);
      
      // Clear temp values and close the modal
      setTempFormValues({});
      closeConfigModal();
    } catch (error) {
      console.error('Error configuring finance node:', error);
      notifyError(error, { operation: needsReconfiguration ? OP.node.reconfigure : OP.node.editFinance });
      setIsConfiguring(false);
    }
  }, [id, data, edges, nodes, session, needsReconfiguration, getInputNodeName, getNodePosition, updateNodeMapping, addFinanceNode, reactFlowInstance, updateSelectedNode, closeConfigModal]);

  return (
    <div 
      className={`finance-node ${selected ? 'finance-selected' : ''} ${
        !isConfigured 
          ? 'finance-not-configured' 
            : needsReconfiguration 
            ? 'finance-needs-reconfiguration' 
            : 'finance-configured'
      }`}
    >
      <Handle
        type="target"
        position={Position.Left}
        className={`finance-connection-point ${
          !isConfigured 
            ? 'finance-not-configured' 
            : needsReconfiguration 
              ? 'finance-needs-reconfiguration' 
              : 'finance-configured'
        }`}
      />
      
      <div 
        className={`finance-node-header ${
          !isConfigured 
            ? 'finance-not-configured' 
            : needsReconfiguration 
              ? 'finance-needs-reconfiguration' 
              : 'finance-configured'
        }`}
      >
        <div className="finance-node-header-title">
          <div className="finance-node-type">Finance</div>
          {name && <div className="finance-node-name-display">{name}</div>}
        </div>
        
        <div className="finance-node-header-icons">
          {needsReconfiguration ? (
            <button 
              onClick={handleReconfigureInput}
              className="finance-config-button finance-reconfigure-button"
              title="Reconfigure input node"
              disabled={!hasInputConnection || isConfiguring}
            >
              <GrConfigure className="finance-icon" />
            </button>
          ) : (
            <button 
              onClick={isConfigured ? () => openConfigModal(true) : openConfigModal}
              className="finance-config-button"
              title={isConfigured ? "Edit Configuration" : hasInputConnection ? "Configure Finance" : "Connect an input first"}
              disabled={(!hasInputConnection && !isConfigured) || isConfiguring}
            >
              {isConfigured ? (
                <Pencil1Icon className="finance-icon" />
              ) : (
                <ExclamationTriangleIcon className="finance-icon" />
              )}
            </button>
          )}
          <GiMoneyStack className="finance-icon" />
        </div>
      </div>
      
      <div className="finance-node-content">
        {!isConfigured ? (
          <div className="finance-text-warning">
            Not configured. Click the gear icon to configure.
          </div>
        ) : needsReconfiguration ? (
          <div className="finance-text-warning">
            Input node changed. Click the warning icon to reconfigure.
          </div>
        ) : (
          <>
            <div className="finance-flex-container">
              <span className="finance-text-xs">Computed Discom Tariff:</span>
              <span>{displayTariff}</span>
            </div>
          </>
        )}
      </div>
      
      <Handle
          type="source"
        position={Position.Right}
        className={`finance-connection-point ${
          !isConfigured 
            ? 'finance-not-configured' 
            : needsReconfiguration 
              ? 'finance-needs-reconfiguration-output' 
              : 'finance-configured-output'
        }`}
      />
      
      {isModalOpen && (
        <FinanceConfigModal
          isOpen={isModalOpen}
          onClose={closeConfigModal}
          onConfigured={handleConfigured}
          initialData={{
            name: tempFormValues.name || data.name || '',
            perUnitDiscomCost: tempFormValues.perUnitDiscomCost || data.perUnitDiscomCost || '',
            
            inputNodeName: inputNodeName,
            position: getNodePosition(),
            isReconfiguring: needsReconfiguration,
            isEditing: isConfigured && !needsReconfiguration
          }}
        />
      )}
    </div>
  );
};

export default FinanceNode;